import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  //User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import {
  doc,
  setDoc,
  Timestamp,
  // serverTimestamp,
  // collection,
  // query,
  // where,
  // getDocs,
  // updateDoc
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase/config';
import { AuthContextType, User, UserRole, UserStatus, withImpliedRoles } from '../lib/types/auth';
//import { toast } from 'react-hot-toast';
import { safeGetDoc } from '../utils/firestore';
import { chatNotificationService } from '../services/chatNotificationService';
import { notificationService } from '../services/notificationService';
import { userActivityService } from '../services/userActivityService';

// Create context with a default value
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  error: null,
  login: async (): Promise<void> => { throw new Error('AuthContext not initialized') },
  register: async (): Promise<void> => { throw new Error('AuthContext not initialized') },
  logout: async (): Promise<void> => { throw new Error('AuthContext not initialized') },
  clearError: () => {},
  refreshUserData: async (): Promise<void> => { throw new Error('AuthContext not initialized') }
});

// Loading spinner component
//const LoadingSpinner = () => (
//  <div className="flex h-screen items-center justify-center">
//    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
//  </div>
//);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const clearError = () => setError(null);

  // Set up session-only persistence
  useEffect(() => {
    const setupSessionPersistence = async () => {
      try {
        await setPersistence(auth, browserSessionPersistence);
      } catch (error) {
        // Silently handle persistence setup errors
      }
    };
    
    setupSessionPersistence();
  }, []);

  // Handle browser/tab close to ensure logout
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentUser) {
        // Clean up user activity tracking
        try {
          userActivityService.cleanup();
        } catch {
          // Silently handle cleanup errors
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && currentUser) {
        // User switched away from tab, mark as potentially offline
        try {
          userActivityService.markUserInactive();
        } catch {
          // Silently handle errors
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const data = await safeGetDoc(userDocRef, 3, 2000, 'Failed to fetch user data');
          
          if (data) {
            const userData: User = {
              uid: firebaseUser.uid,
              email: data.email || '',
              displayName: data.displayName || '',
              roles: Array.isArray(data.roles)
                ? withImpliedRoles(data.roles.map((role: string) => role as UserRole))
                : [],
              createdAt: data.createdAt,
              lastLogin: data.lastLogin,
              status: data.status,
              storeId: data.storeId,
              photoURL: data.photoURL || null,
              soundboardAccess: data.soundboardAccess === true,
              instantConnectAccess: data.instantConnectAccess === true,
              storeCalendarAccess: data.storeCalendarAccess === true,
              jewelerAccess: data.jewelerAccess === true,
              claireAccess: data.claireAccess === true,
              bulletinBoardAccess: data.bulletinBoardAccess === true,
              floaterAccess: data.floaterAccess === true,
              excludedPages: Array.isArray(data.excludedPages) ? data.excludedPages : [],
            };
            setCurrentUser(userData);
            
            // Cache user data for PWA refresh checks
            try {
              localStorage.setItem('fin_user_data', JSON.stringify({ roles: userData.roles }));
            } catch (e) {
              // Ignore localStorage errors
            }
            
            // Initialize chat notifications for this user
            if (userData.email) {
              chatNotificationService.initializeChatNotifications(userData.email);
            }

            // Initialize desktop notifications (optional, user can enable later)
            notificationService.initializeDesktopNotifications().catch(error => {
              console.log('Desktop notifications not available or permission denied:', error);
            });

            // Initialize user activity tracking with delay to avoid auth interference
            setTimeout(() => {
              userActivityService.initializeTracking(firebaseUser).catch(() => {
                // Silently handle initialization errors to avoid auth interference
              });
            }, 2000); // 2 second delay to ensure auth is settled
          } else {
            console.warn('Auth state changed: User document not found');
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
          // Clear cached user data
          try {
            localStorage.removeItem('fin_user_data');
          } catch (e) {
            // Ignore localStorage errors
          }
          // Clean up chat notifications when user logs out
          if (currentUser?.email) {
            chatNotificationService.cleanup(currentUser.email);
          }
          
          // Clean up user activity tracking when user logs out
          try {
            userActivityService.cleanup();
          } catch {
            // Silently handle cleanup errors
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch user data'));
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Session management and auto-logout
  useEffect(() => {
    // Set session flag when user logs in
    if (currentUser) {
      sessionStorage.setItem('user_session_active', 'true');
      sessionStorage.setItem('last_activity', Date.now().toString());
    } else {
      sessionStorage.removeItem('user_session_active');
      sessionStorage.removeItem('last_activity');
    }
  }, [currentUser]);

  // Check for session validity on focus/visibility
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser) {
        const sessionActive = sessionStorage.getItem('user_session_active');
        const lastActivity = sessionStorage.getItem('last_activity');
        
        if (!sessionActive || !lastActivity) {
          console.log('Session expired - logging out user');
          signOut(auth);
          setCurrentUser(null);
          return;
        }

        // Update last activity
        sessionStorage.setItem('last_activity', Date.now().toString());
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      } else if (document.visibilityState === 'hidden' && currentUser) {
        // Set a flag that we're going hidden
        sessionStorage.setItem('went_hidden_at', Date.now().toString());
      }
    };

    // Activity tracking for session timeout
    const updateActivity = () => {
      if (currentUser) {
        sessionStorage.setItem('last_activity', Date.now().toString());
      }
    };

    // Helper function to check if user should be excluded from auto-logout
    const isExemptFromAutoLogout = (user: User): boolean => {
      if (!user || !user.roles) return false;
      return user.roles.includes(UserRole.MasterAdmin) || 
             user.roles.includes(UserRole.CSAdmin) ||
             user.roles.includes(UserRole.Template) ||
             user.roles.includes(UserRole.MarketingAdmin) ||
             user.roles.includes(UserRole.Marketing) ||
             user.roles.includes(UserRole.WatchAdmin);
    };

    // Check for session timeout periodically
    const sessionCheck = setInterval(() => {
      if (currentUser) {
        // Skip timeout check for Master Admin, CS Admin, Template, Marketing Admin, Marketing, and Watch Admin users
        if (isExemptFromAutoLogout(currentUser)) {
          return;
        }

        const lastActivity = sessionStorage.getItem('last_activity');
        const wentHiddenAt = sessionStorage.getItem('went_hidden_at');
        
        if (lastActivity) {
          const timeSinceActivity = Date.now() - parseInt(lastActivity);
          const timeSinceHidden = wentHiddenAt ? Date.now() - parseInt(wentHiddenAt) : 0;
          
          // Logout after 2 hours of inactivity or 30 minutes if hidden
          const timeoutThreshold = document.visibilityState === 'hidden' ? 1800000 : 7200000; // 30 min hidden, 2 hours visible
          
                      if (timeSinceActivity > timeoutThreshold || (wentHiddenAt && timeSinceHidden > 1800000)) {
            console.log('Session timeout - logging out user');
            signOut(auth);
            setCurrentUser(null);
            sessionStorage.removeItem('user_session_active');
            sessionStorage.removeItem('last_activity');
            sessionStorage.removeItem('went_hidden_at');
          }
        }
      }
    }, 60000); // Check every minute

    if (currentUser) {
      // Add event listeners
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
      });
    }

    return () => {
      clearInterval(sessionCheck);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [currentUser]);

  
  // Login handler
  const login = async (identifier: string, password: string | null, options?: { smsVerified?: boolean; token?: string; userData?: any }): Promise<void> => {
    try {
      clearError();
      
      let user: any;
      let userData: any;
      
      if (options?.smsVerified && options?.userData) {
        // SMS verification login - user data already provided from Firebase Phone Auth
        userData = options.userData;
        user = { uid: userData.uid };
        
        // The user is already authenticated with Firebase through phone auth
        // No need to call signInWithEmailAndPassword
      } else {
        // Regular email/password login
        const userCredential = await signInWithEmailAndPassword(auth, identifier, password!);
        user = userCredential.user;
        
        const userDocRef = doc(db, 'users', user.uid);
        const data = await safeGetDoc(userDocRef, 3, 2000, 'Failed to fetch user data');
        userData = data;
      }

      if (userData) {
        // Check if user status allows login
        if (userData.status === UserStatus.Pending) {
          // Sign out the user since they're not approved yet
          if (!options?.smsVerified) await signOut(auth);
          throw new Error('Your account is pending approval. Please contact an administrator.');
        }
        
        if (userData.status === UserStatus.Suspended) {
          // Sign out the user since they're suspended
          if (!options?.smsVerified) await signOut(auth);
          throw new Error('Your account has been suspended. Please contact an administrator.');
        }
        
        if (userData.status === UserStatus.Deleted) {
          // Sign out the user since their account is deleted
          if (!options?.smsVerified) await signOut(auth);
          throw new Error('Your account has been deleted. Please contact an administrator.');
        }
        
        // Only allow Active users to login
        if (userData.status !== UserStatus.Active) {
          if (!options?.smsVerified) await signOut(auth);
          throw new Error('Your account status does not allow login. Please contact an administrator.');
        }

        // Update last login timestamp
        if (!options?.smsVerified) {
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, {
            lastLogin: Timestamp.now()
          }, { merge: true });
        }

        const updatedUser: User = {
          uid: user.uid,
          email: userData.email || '',
          displayName: userData.displayName || userData.name || '',
          roles: withImpliedRoles(userData.roles ? userData.roles.map((role: string) => role as UserRole) : userData.role ? [userData.role as UserRole] : []),
          createdAt: userData.createdAt,
          lastLogin: Timestamp.now(),
          status: userData.status,
          storeId: userData.storeId,
          photoURL: userData.photoURL || null,
          soundboardAccess: userData.soundboardAccess === true,
          instantConnectAccess: userData.instantConnectAccess === true,
          storeCalendarAccess: userData.storeCalendarAccess === true,
          jewelerAccess: userData.jewelerAccess === true,
          claireAccess: userData.claireAccess === true,
          bulletinBoardAccess: userData.bulletinBoardAccess === true,
          floaterAccess: userData.floaterAccess === true,
          excludedPages: Array.isArray(userData.excludedPages) ? userData.excludedPages : []
        };

        setCurrentUser(updatedUser);

        // Initialize user activity tracking (temporarily disabled)
        // userActivityService.initializeTracking(user).catch(error => {
        //   console.log('User activity tracking failed to initialize:', error);
        // });
      } else {
        throw new Error('User document not found');
      }
    } catch (err) {
      const error = new Error(
        err instanceof Error 
          ? err.message
          : typeof err === 'object' && err && 'code' in err
            ? (err as any).code === 'auth/invalid-credential'
              ? 'Invalid email or password'
              : (err as any).code === 'auth/network-request-failed'
                ? 'Network error. Please check your connection'
                : 'Failed to login'
            : 'Failed to login'
      );
      setError(error);
      throw error;
    }
  };
  
  // Register handler
  const register = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      clearError();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;
      
      const timestamp = Timestamp.now();
      const userData: User = {
        uid: user.uid,
        email: user.email || '',
        displayName: displayName,
        roles: [UserRole.SalesRep],
        createdAt: timestamp,
        lastLogin: timestamp,
        status: UserStatus.Pending,
        storeId: null,
        photoURL: null
      };

      await setDoc(doc(db, 'users', user.uid), userData);
      
      // Sign out the user immediately after registration since they need approval
      await signOut(auth);
      
      // Do NOT set currentUser - they need admin approval first
    } catch (err) {
      const error = new Error(
        err instanceof Error 
          ? err.message
          : typeof err === 'object' && err && 'code' in err
            ? (err as any).code === 'auth/email-already-in-use'
              ? 'This email is already registered'
              : (err as any).code === 'auth/network-request-failed'
                ? 'Network error. Please check your connection'
                : 'Failed to register'
            : 'Failed to register'
      );
      setError(error);
      throw error;
    }
  };
  
  // Logout handler
  const logout = async () => {
    try {
      clearError();
      
      // Clean up user activity tracking before logging out
      try {
        userActivityService.cleanup();
      } catch {
        // Silently handle cleanup errors
      }
      
      await signOut(auth);
      setCurrentUser(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to logout');
      setError(error);
      throw error;
    }
  };
  
  // Refresh user data
  const refreshUserData = async (): Promise<void> => {
    if (!currentUser) {
      console.log('refreshUserData: No user is currently logged in');
      throw new Error('No user is currently logged in');
    }

    try {
      console.log('refreshUserData: Fetching user data for', currentUser.uid);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const data = await safeGetDoc(userDocRef, 3, 2000, 'Failed to fetch user data');
      
      if (data) {
        console.log('refreshUserData: Got user data', {
          photoURL: data.photoURL,
          displayName: data.displayName
        });
        
        const updatedUser: User = {
          uid: currentUser.uid,
          email: data.email || '',
          displayName: data.displayName || '',
          roles: withImpliedRoles(data.roles.map((role: string) => role as UserRole)),
          createdAt: data.createdAt,
          lastLogin: data.lastLogin,
          status: data.status,
          storeId: data.storeId,
          photoURL: data.photoURL || null,
          soundboardAccess: data.soundboardAccess === true,
          instantConnectAccess: data.instantConnectAccess === true,
          storeCalendarAccess: data.storeCalendarAccess === true,
          jewelerAccess: data.jewelerAccess === true,
          claireAccess: data.claireAccess === true,
          bulletinBoardAccess: data.bulletinBoardAccess === true,
          floaterAccess: data.floaterAccess === true,
          excludedPages: Array.isArray(data.excludedPages) ? data.excludedPages : []
        };
        console.log('refreshUserData: Setting updated user data');
        setCurrentUser(updatedUser);
      } else {
        console.warn('refreshUserData: User document not found');
        throw new Error('User document not found');
      }
    } catch (err) {
      console.error('refreshUserData error:', err);
      const error = new Error('Failed to refresh user data');
      setError(error);
      throw error;
    }
  };
  
  const value: AuthContextType = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    refreshUserData
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Remove unused function at the end
// const ensureValidRole = (role: string): UserRole => {
//   switch (role) {
//     case 'MasterAdmin':
//       return UserRole.MasterAdmin;
//     case 'StoreStaff':
//       return UserRole.StoreStaff;
//     case 'Manager':
//       return UserRole.Manager;
//     case 'CSAdmin':
//       return UserRole.CSAdmin;
//     case 'SalesAdmin':
//       return UserRole.SalesAdmin;
//     case 'SalesRep':
//       return UserRole.SalesRep;
//     default:
//       return UserRole.StoreStaff;
//   }
// };
